/**
 * Run status enumeration
 */
export type RunStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED'
  | 'TIMEOUT'

/**
 * Run artifacts containing URLs to logs, diff, and test results
 */
export interface RunArtifacts {
  logs?: string
  diff?: string
  tests?: string
}

/**
 * Run timestamps for lifecycle events
 */
export interface RunTimestamps {
  /** When the run was created */
  created: string
  /** When the run started executing */
  started?: string
  /** When the run finished */
  finished?: string
}

/**
 * Run model matching agentic-server Run schema
 */
export interface Run {
  /** Unique run identifier (e.g., run_20251219_143052_a1b2c3d4) */
  run_id: string
  /** Current status of the run */
  status: RunStatus
  /** Git repository URL */
  repo_url: string
  /** Base branch name */
  base_branch: string
  /** Working branch name (if different from base) */
  branch?: string
  /** Goal/task description for the agent */
  goal: string
  /** Optional constraints for the agent */
  constraints?: string[]
  /** Runner profile to use */
  runner_profile?: string
  /** Timeout in seconds */
  timeout_seconds?: number
  /** Azure DevOps project name */
  ado_project: string
  /** Azure DevOps repository ID */
  ado_repo_id: string
  /** Associated work item ID (e.g., AB#12345) */
  work_item_id?: string
  /** Pull request URL (populated after successful run) */
  pr_url?: string
  /** Run artifacts (logs, diff, test results) */
  artifacts?: RunArtifacts
  /** Run lifecycle timestamps */
  timestamps?: RunTimestamps
  /** Error message if run failed */
  error_message?: string
}

/**
 * Request payload for creating a new run
 */
export interface CreateRunRequest {
  repo_url: string
  base_branch: string
  goal: string
  constraints?: string[]
  runner_profile?: string
  timeout_seconds?: number
  ado_project: string
  ado_repo_id: string
  work_item_id?: string
}

/**
 * Response from GET /runs endpoint
 */
export interface RunsListResponse {
  runs: Run[]
  total: number
  offset: number
  limit: number
}

/**
 * Parameters for filtering runs
 */
export interface RunsFilterParams {
  status?: RunStatus[]
  search?: string
  start_date?: string
  end_date?: string
  offset?: number
  limit?: number
  /** Filter by project ID */
  projectId?: string
}

/**
 * Log entry from streaming
 */
export interface LogEntry {
  timestamp: string
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'Agent'
  message: string
}

/**
 * Logs response from GET /runs/{id}/logs
 */
export interface RunLogsResponse {
  run_id: string
  logs: string
  is_streaming: boolean
}

/**
 * Agent event types captured during execution
 */
export type AgentEventType =
  | 'agent_started'
  | 'file_read'
  | 'file_modified'
  | 'command_executed'
  | 'error'
  | 'run_completed'
  | 'iteration_completed'
  | 'discovery_started'
  | 'scout_iteration'
  | 'evidence_found'
  | 'discovery_complete'
  | 'discovery_extended'

/**
 * A milestone event captured during agent execution
 */
export interface AgentEvent {
  /** Type of the event */
  event_type: AgentEventType
  /** When the event occurred (ISO timestamp) */
  timestamp: string
  /** Event-specific metadata */
  metadata: Record<string, unknown>
}

/**
 * Response from GET /runs/{id}/events
 */
export interface RunEventsResponse {
  run_id: string
  events: AgentEvent[]
}

// ============================================================================
// Orchestrator Session Types (matching C# API models)
// ============================================================================

/**
 * Workflow phase names for background session execution.
 * Maps to WorkflowPhase constants in the orchestrator.
 */
export type WorkflowPhase = 'planning' | 'coding' | 'testing' | 'reviewing'

/**
 * All workflow phases in order of execution.
 */
export const WORKFLOW_PHASES: readonly WorkflowPhase[] = [
  'planning',
  'coding',
  'testing',
  'reviewing',
] as const

/**
 * Visual status for a workflow phase in the progress UI.
 */
export type WorkflowPhaseStatus =
  | 'completed' // Phase finished successfully
  | 'active'    // Phase currently executing
  | 'pending'   // Phase not yet started
  | 'skipped'   // Phase was skipped
  | 'error'     // Phase failed with error

/**
 * Record of a completed or in-progress workflow phase.
 * Maps to PhaseRecordDto from orchestrator API.
 */
export interface PhaseRecord {
  /** Phase name (planning, coding, testing, reviewing) */
  phase: WorkflowPhase
  /** When the phase started (ISO timestamp) */
  startedAt: string
  /** When the phase completed (ISO timestamp, null if in progress) */
  completedAt?: string | null
}

/**
 * Session status matching SessionStatus enum from orchestrator API.
 * Includes both interactive and background session statuses.
 */
export type SessionStatus =
  | 'Idle'           // Session exists but no orchestration is running
  | 'Running'        // Orchestration is currently in progress
  | 'WaitingForInput'// Agent is waiting for user input
  | 'Completed'      // Orchestration completed successfully
  | 'Failed'         // Orchestration failed with an error
  | 'Timeout'        // Orchestration timed out (can be resumed)
  | 'Queued'         // Background: waiting to be picked up
  | 'Paused'         // Background: manually paused by user
  | 'Resumable'      // Background: can be resumed after crash/restart
  | 'Cancelled'      // Background: cancelled by user request

/**
 * Execution mode for a session.
 */
export type SessionMode =
  | 'Interactive'    // Real-time SSE-bound execution
  | 'Background'     // Fire-and-forget execution with checkpoints
  | 'Chatbot'        // Platform Assistant chatbot session

/**
 * Checkpoint state for resumable background sessions.
 * Maps to CheckpointDto from orchestrator API.
 */
export interface ExecutionCheckpoint {
  /** Current execution phase */
  currentPhase: string
  /** Step index within the phase */
  stepIndex: number
  /** Name of the current agent */
  currentAgentName?: string
  /** Current activity within the phase (e.g., "Discovery - Scout iteration 3") */
  currentActivity?: string
  /** Last completed action description */
  lastCompletedAction?: string
  /** State data for resumption */
  stateData?: Record<string, unknown>
  /** When the checkpoint was created */
  updatedAt: string
  /** History of completed phases with timestamps */
  completedPhases?: PhaseRecord[]
}

/**
 * Session metadata matching SessionMetadataResponse from orchestrator API
 */
export interface SessionMetadata {
  /** Unique session identifier */
  sessionId: string
  /** When the session was created */
  createdAt: string
  /** When the session was last updated */
  updatedAt: string
  /** Number of messages in the session */
  messageCount: number
  /** Last user message (truncated) */
  lastUserMessage?: string
  /** Current status of the session */
  status: SessionStatus
  /** Time-to-live duration in seconds */
  timeToLive?: number | null
  /** When the session expires */
  expiresAt?: string
  /** Whether the session is expiring soon */
  isExpiringSoon: boolean
  /** The current working branch for this session */
  workBranch?: string
  /** The PR URL associated with this session */
  prUrl?: string
  /** Optional project ID that this session belongs to */
  projectId?: string
  /** Project name for display (if associated with a project) */
  projectName?: string
  /** Whether the associated project has been archived */
  isProjectArchived?: boolean

  // ─────────────────────────────────────────────────────────────────────────
  // Author Information (who started the session)
  // ─────────────────────────────────────────────────────────────────────────

  /** User OID who created/started the session */
  createdBy?: string
  /** Display name of user who created the session */
  createdByName?: string
  /** Email of user who created the session */
  createdByEmail?: string

  // ─────────────────────────────────────────────────────────────────────────
  // Constraints
  // ─────────────────────────────────────────────────────────────────────────

  /** Session-level constraints (e.g., "scope:path:src/**", "no-delete") */
  constraints?: string[]

  // ─────────────────────────────────────────────────────────────────────────
  // Background Session Fields
  // ─────────────────────────────────────────────────────────────────────────

  /** Execution mode (Interactive or Background) */
  mode?: SessionMode
  /** Current checkpoint for background sessions */
  checkpoint?: ExecutionCheckpoint
  /** Error message if session failed */
  errorMessage?: string
  /** Detailed error information */
  errorDetails?: string
  /** ID of the orchestrator instance owning this session */
  claimedBy?: string
  /** Last heartbeat timestamp for background sessions */
  lastHeartbeat?: string

  // ─────────────────────────────────────────────────────────────────────────
  // Token Usage
  // ─────────────────────────────────────────────────────────────────────────

  /** Total token usage for this session (SK + OpenHands combined) */
  totalTokens?: number
}

/**
 * Session message matching SessionMessageResponse from orchestrator API
 */
export interface SessionMessage {
  /** Message role: 'user', 'assistant', 'system', or 'tool' */
  role: 'user' | 'assistant' | 'system' | 'tool'
  /** Message content */
  content: string
  /** Agent name (Planner, Coder, Reviewer, etc.) from SK AuthorName - Phase 9 */
  agentName?: string
  /** Message timestamp (optional for display) */
  timestamp?: string
}

/**
 * Execution event that occurred during session processing
 * Matches SessionEventResponse from orchestrator API
 */
export interface SessionEvent {
  /** Event type (e.g., "discovery_started", "scout_iteration") */
  eventType: string
  /** Human-readable message */
  message?: string
  /** Additional structured data */
  data?: Record<string, unknown>
  /** When the event occurred */
  timestamp: string
}

/**
 * Full session with messages matching GetSessionResponse from orchestrator API
 */
export interface Session {
  /** Session metadata */
  metadata: SessionMetadata
  /** Session messages (chat history, paginated) */
  messages: SessionMessage[]
  /** Total number of messages in the conversation */
  totalCount?: number
  /** Whether there are more (older) messages to load */
  hasMore?: boolean
  /** Execution events (discovery, planning, etc.) */
  events?: SessionEvent[]
}

/**
 * Response from GET /sessions matching ListSessionsResponse from orchestrator API
 */
export interface SessionsListResponse {
  /** List of sessions */
  sessions: SessionMetadata[]
  /** Total number of sessions */
  total: number
  /** Whether there are more sessions to fetch */
  hasMore: boolean
}

/**
 * Parameters for listing sessions
 */
export interface SessionsFilterParams {
  /** Maximum number of sessions to return */
  limit?: number
  /** Number of sessions to skip */
  offset?: number
  /** Include expired sessions */
  includeExpired?: boolean
  /** Filter by project ID */
  projectId?: string
  /** Full-text search in session's last user message */
  search?: string
  /** Filter by one or more session statuses (multi-select) */
  status?: SessionStatus[]
  /** Filter by session mode */
  mode?: SessionMode
  /** Exclude sessions with this mode from results */
  excludeMode?: SessionMode
  /** Filter sessions created on or after this date */
  dateFrom?: Date
  /** Filter sessions created on or before this date */
  dateTo?: Date
}

/**
 * Daily activity item for session statistics
 */
export interface DailyActivityItem {
  /** Date in ISO format (yyyy-MM-dd) */
  date: string
  /** Number of sessions created on this date */
  count: number
}

/**
 * Session statistics response from GET /sessions/stats
 */
export interface SessionStatsResponse {
  /** Total number of sessions */
  total: number
  /** Count of sessions by status */
  statusCounts: Record<string, number>
  /** Daily activity for the last 7 days */
  dailyActivity: DailyActivityItem[]
}

/**
 * Parameters for fetching session statistics
 */
export interface SessionStatsParams {
  /** Optional project ID to filter stats by */
  projectId?: string
}

// ============================================================================
// Orchestrator Streaming Types (matching C# OrchestrationEvent)
// ============================================================================

/**
 * Orchestration event types matching OrchestrationEventType enum
 */
export type OrchestrationEventType =
  | 'agent_started'
  | 'progress_update'
  | 'tool_called'
  | 'sub_agent_message'
  | 'complete'
  | 'error'
  | 'discovery_started'
  | 'discovery_skipped'
  | 'scout_iteration'
  | 'evidence_found'
  | 'discovery_complete'
  | 'discovery_extended'
  | 'supervisor_policy_decision'
  | 'supervisor_retry_attempt'
  | 'supervisor_guardian_iteration'
  | 'supervisor_subtask_completed'
  | 'supervisor_evidence_truncated'
  | 'supervisor_auto_action'

/**
 * Orchestration event matching OrchestrationEvent from orchestrator API
 */
export interface OrchestrationEvent {
  /** Type of the event */
  eventType: OrchestrationEventType
  /** Human-readable message */
  message: string
  /** Event timestamp */
  timestamp: string
  /** Optional additional data (varies by event type) */
  data?: unknown
}

/**
 * Request for streaming orchestration matching StreamOrchestrationRequest
 */
export interface StreamOrchestrationRequest {
  /** The user's request/question */
  userRequest: string
  /** Optional session ID to continue existing conversation */
  sessionId?: string
  /** Optional project ID to associate session with */
  projectId?: string
  /** Optional session-level constraints (merged with project constraints) */
  constraints?: string[]
}

// ============================================================================
// Project Types (matching C# API models)
// ============================================================================

/**
 * Full project definition matching ProjectResponse from orchestrator API
 */
export interface Project {
  /** Unique project identifier */
  id: string
  /** Human-readable project name */
  name: string
  /** Optional description of the project */
  description?: string
  /** Default repository URL for sessions */
  defaultRepository?: string
  /** Default branch name for sessions */
  defaultBranch?: string
  /** Default constraints inherited by sessions */
  constraints: string[]
  /** Context template prepended to system prompt */
  contextTemplate?: string
  /** Project-level context items (knowledge base) */
  contextItems?: ProjectContextItem[]
  /** Whether the project is archived (soft-deleted) */
  isArchived: boolean
  /** When the project was created */
  createdAt: string
  /** When the project was last updated */
  updatedAt: string
  /** Total number of sessions under this project */
  sessionCount: number
  /** Number of active (Running, Queued, WaitingForInput, Resumable, Paused) sessions */
  activeSessionCount: number
  /** Number of failed sessions under this project */
  failedSessionCount: number
}

/**
 * Project-level context item for knowledge base.
 * Uses type/value strings for maximum extensibility without schema changes.
 */
export interface ProjectContextItem {
  /** Type using namespaced convention: "wiki:adr", "wiki:arch", "epic", "file", etc. */
  type: string
  /** Value/identifier: "/docs/decisions/001", "Epic-12345", "src/core/auth.ts" */
  value: string
  /** Optional note or description for the context item */
  note?: string
  /** When the item was added */
  addedAt: string
}

/**
 * Input for creating/updating project context items (no addedAt - server sets it)
 */
export interface ProjectContextItemInput {
  /** Type using namespaced convention */
  type: string
  /** Value/identifier */
  value: string
  /** Optional note or description */
  note?: string
}

/**
 * Project summary for list views (lighter than full Project)
 */
export interface ProjectSummary {
  /** Unique project identifier */
  id: string
  /** Human-readable project name */
  name: string
  /** Optional description */
  description?: string
  /** Default repository URL */
  defaultRepository?: string
  /** Whether the project is archived */
  isArchived: boolean
  /** When the project was created */
  createdAt: string
  /** Total number of sessions */
  sessionCount: number
  /** Number of active sessions (Running, Queued, WaitingForInput, Resumable, Paused) */
  activeSessionCount: number
  /** Number of failed sessions */
  failedSessionCount: number
}

/**
 * Request payload for creating a new project
 */
export interface CreateProjectRequest {
  /** Human-readable project name (required) */
  name: string
  /** Optional description */
  description?: string
  /** Default repository URL for sessions */
  defaultRepository?: string
  /** Default branch name */
  defaultBranch?: string
  /** Default constraints for sessions */
  constraints?: string[]
  /** Context template for system prompt */
  contextTemplate?: string
  /** Project-level context items (knowledge base) */
  contextItems?: ProjectContextItemInput[]
}

/**
 * Request payload for updating a project
 */
export interface UpdateProjectRequest {
  /** Human-readable project name (required) */
  name: string
  /** Optional description */
  description?: string
  /** Default repository URL */
  defaultRepository?: string
  /** Default branch name */
  defaultBranch?: string
  /** Default constraints */
  constraints?: string[]
  /** Context template */
  contextTemplate?: string
  /** Project-level context items (knowledge base) */
  contextItems?: ProjectContextItemInput[]
}

/**
 * Response from GET /projects endpoint
 */
export interface ProjectsListResponse {
  /** List of project summaries */
  projects: ProjectSummary[]
  /** Total number of projects */
  total: number
  /** Whether there are more projects to fetch */
  hasMore: boolean
}

/**
 * Parameters for listing projects
 */
export interface ProjectsFilterParams {
  /** Maximum number of projects to return */
  limit?: number
  /** Number of projects to skip */
  offset?: number
  /** Include archived projects */
  includeArchived?: boolean
}

// =============================================================================
// Prompt Enhancement Types
// =============================================================================

/**
 * Request payload for enhancing a task prompt
 */
export interface EnhancePromptRequest {
  /** The original prompt to enhance */
  prompt: string
  /** Optional project ID for context */
  projectId?: string
  /** Existing constraints (to avoid suggesting duplicates) */
  constraints?: string[]
}

/**
 * Suggested constraint with explanation
 */
export interface ConstraintSuggestion {
  /** The constraint in format category:subcategory:description */
  constraint: string
  /** Why this constraint is recommended */
  reason: string
}

/**
 * Response from prompt enhancement endpoint
 */
export interface EnhancePromptResponse {
  /** The enhanced prompt */
  enhancedPrompt: string
  /** Suggested constraints for the task */
  suggestedConstraints: ConstraintSuggestion[]
  /** Error message if enhancement failed */
  error?: string
}

// ============================================================================
// Service Catalog Types
// ============================================================================

/**
 * Summary view of a service for list display
 */
export interface ServiceSummary {
  /** Service name */
  name: string
  /** Brief description of the service */
  description: string | null
  /** Team that owns this service */
  ownerTeam: string | null
  /** Area path for work item association */
  areaPath: string | null
  /** Technologies used by this service */
  techStack: string[] | null
  /** Alternative names for the service */
  aliases: string[] | null
  /** Country this service is specific to (e.g., Albania, Poland), null for global */
  country: string | null
}

/**
 * Full detail view of a service
 */
export interface ServiceDetail {
  /** Service name */
  name: string
  /** Alternative names for the service */
  aliases: string[] | null
  /** Full description of the service */
  description: string | null
  /** Team that owns this service */
  ownerTeam: string | null
  /** Contact information for owners */
  ownerContacts: string[] | null
  /** Repository URL */
  repository: string | null
  /** Wiki documentation page */
  wikiPage: string | null
  /** Area path for work item association */
  areaPath: string | null
  /** Technologies used by this service */
  techStack: string[] | null
  /** Other services this service depends on */
  dependencies: string[] | null
  /** Keywords for search */
  keywords: string[] | null
  /** Regex patterns for error detection */
  errorPatterns: string[] | null
  /** Internal components of the service */
  components: string[] | null
  /** Short summary of what the service does */
  purpose: string | null
  /** List of main capabilities/features */
  keyFeatures: string[] | null
  /** Country this service is specific to (e.g., Albania, Poland), null for global */
  country: string | null
}

/**
 * Available filter options for the service catalog
 */
export interface ServiceCatalogFilters {
  /** Unique area paths available for filtering */
  areaPaths: string[]
  /** Unique owner teams available for filtering */
  ownerTeams: string[]
  /** Unique countries available for filtering */
  countries: string[]
}

/**
 * Response from GET /service-catalog endpoint
 */
export interface ServiceCatalogResponse {
  /** List of services */
  services: ServiceSummary[]
  /** Total count of services matching the filter */
  total: number
  /** Available filter options */
  availableFilters: ServiceCatalogFilters
}

/**
 * Parameters for filtering the service catalog
 */
export interface ServiceCatalogParams {
  /** Search query (searches name, aliases, description, keywords) */
  search?: string
  /** Filter by area path */
  areaPath?: string
  /** Filter by owner team */
  ownerTeam?: string
  /** Filter by country (use "Global" for services without a specific country) */
  country?: string
  /** Maximum number of services to return */
  limit?: number
  /** Offset for pagination */
  offset?: number
}
